import { useState, useMemo } from "react";

/**
 * Provides client-side sorting for a data array.
 *
 * @param {Array}  data             - The array to sort (pre-filtered is fine)
 * @param {string} defaultKey       - Initial sort column key
 * @param {string} defaultDirection - "asc" | "desc" (default: "asc")
 *
 * Returns: { sorted, sortKey, sortDir, handleSort(key) }
 */
const useSortableTable = (data, defaultKey, defaultDirection = "asc") => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDirection);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !data) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const cmp =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal ?? "").localeCompare(String(bVal ?? ""));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, handleSort };
};

export default useSortableTable;
